import { FormBlockInstance } from '../api-types';

export const DATE_PICKER_TYPE = 'date_picker';
export const DATE_PICKER_TYPE_VERSION = 1;

/** `YYYY-MM-DD`, the only shape pro-core accepts and the only one a native date input emits. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export interface DatePickerConfig {
  minDate: string | null;
  maxDate: string | null;
}

export interface DatePickerAnswer {
  date: string;
}

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asIsoDate = (value: unknown): string | null =>
  typeof value === 'string' && ISO_DATE.test(value) ? value : null;

export const readDatePickerConfig = (
  block: FormBlockInstance
): DatePickerConfig => {
  const interaction = asRecord(asRecord(block.config).interaction);
  return {
    minDate: asIsoDate(interaction.minDate),
    maxDate: asIsoDate(interaction.maxDate)
  };
};

export const readDatePickerAnswer = (
  answer: unknown
): DatePickerAnswer | null => {
  const date = asIsoDate(asRecord(answer).date);
  return date ? { date } : null;
};

/**
 * Whether the answer is complete enough to move on.
 *
 * The window is checked here as well as on the server, because a native date input enforces `min`
 * and `max` only when the user goes through its picker — typed input is not constrained on every
 * browser. Letting an out-of-range date through would enable Next and then fail at submit.
 */
export const isDatePickerAnswered = (
  answer: unknown,
  config: DatePickerConfig
): boolean => {
  const parsed = readDatePickerAnswer(answer);
  if (!parsed) {
    return false;
  }
  // String comparison is exact for ISO dates: fixed width, zero-padded, most significant first.
  if (config.minDate && parsed.date < config.minDate) {
    return false;
  }
  return !(config.maxDate && parsed.date > config.maxDate);
};
